const URL_RE = /((?:https?:\/\/|www\.)[^\s<]+[^\s<.,;:!?)"'\]])/g

// Digits in groups, as people write a phone number: +353 87 123 4567, (555) 123-4567, 087-123 4567.
// A separator is needed between two runs of digits, so a long run of digits is read one way only.
// Not part of a word, an address, an email or a longer run such as a date (2026-10-14).
const PHONE_RE = /(?<![\w+@/]|\d[.-])\+?(?:\(\d+\) ?)?\d+(?:(?:[ \u00a0.-]| ?\(\d+\) ?)\d+)*(?![\w@/]|[.-]\d)/g

/** A piece of plain text: words as they are, or a web address or phone number with where it goes. */
export type TextPart = { text: string } | { text: string; href: string }

/** Plain text cut into its words and the web addresses and phone numbers in it, in order. */
export function textParts(text: string): TextPart[] {
  return text
    .split(URL_RE)
    .flatMap((part, i) => (i % 2 === 1 ? [{ text: part, href: part.startsWith('www.') ? `https://${part}` : part }] : phoneParts(part)))
    .filter((p) => p.text !== '')
}

function phoneParts(text: string): TextPart[] {
  const parts: TextPart[] = []
  let at = 0
  for (const m of text.matchAll(PHONE_RE)) {
    for (const found of phonesIn(m[0])) {
      const start = m.index + found.start
      parts.push({ text: text.slice(at, start) }, { text: found.text, href: found.href })
      at = start + found.text.length
    }
  }
  parts.push({ text: text.slice(at) })
  return parts
}

/**
 * The phone numbers in a run of digit groups, longest first from the left, so a number next to
 * another figure still counts: "Room 4 087 123 4567" or "087 123 4567 2 times".
 */
function phonesIn(run: string): { text: string; href: string; start: number }[] {
  const words = [...run.matchAll(/[^ \u00a0]+/g)].map((w) => ({ start: w.index, end: w.index + w[0].length }))
  const found: { text: string; href: string; start: number }[] = []
  let i = 0
  while (i < words.length) {
    let next = i + 1
    for (let j = words.length - 1; j >= i; j--) {
      const text = run.slice(words[i].start, words[j].end)
      const href = phoneHref(text)
      if (href) {
        found.push({ text, href, start: words[i].start })
        next = j + 1
        break
      }
    }
    i = next
  }
  return found
}

/**
 * Where a written phone number dials, or null when the digits are not one: a number with its country
 * code (+353 87 123 4567, 00353 87 123 4567, +1 555 123 4567), an Irish number (087 123 4567,
 * 01 234 5678, 1800 123 456) or a US one (555-123-4567, 1-800-555-1234). It dials as written, so the
 * phone's own network reads a number without a country code.
 */
export function phoneHref(written: string): string | null {
  const plus = written.startsWith('+')
  // the 0 kept in brackets for callers at home, +353 (0)87 …, is not dialled from abroad
  const digits = (plus ? written.replace('(0)', '') : written).replace(/\D/g, '')
  if (plus || digits.startsWith('00')) {
    const number = plus ? digits : digits.slice(2)
    return /^[1-9]\d{7,14}$/.test(number) ? `tel:+${number}` : null
  }
  // without a country code, the groups must be shaped like a number too, so a stretch of an account
  // or card number does not count: Irish ones start with 0 or 1800, US ones read 555 123 4567
  const groups = written.match(/\d+/g) ?? []
  const irish = /^0[1-9]\d{7,8}$/.test(digits) || (/^1(?:800|850|890)\d{6}$/.test(digits) && (groups[0]?.length ?? 0) >= 4)
  const shape = (groups[0] === '1' ? groups.slice(1) : groups).map((g) => g.length).join()
  const us = /^1?[2-9]\d{9}$/.test(digits) && ['10', '3,3,4', '3,7'].includes(shape)
  return irish || us ? `tel:${digits}` : null
}

/** Whether a part's link rings a phone rather than opening a page. */
export const isPhone = (href: string) => href.startsWith('tel:')
